import styles from './creditAccountStatusPDFPage.module.css'
import { Customer } from '../../../../sales'
import { addressHelpers } from '../../../../../features/address'
import { useEffect, useMemo, useState } from 'react'
import { Data2, useDataTypesHooks, Labels } from '../../../../../features/data-types'
import { useBranchLoaders } from '../../../../manager'
import { useCustomerLoaders } from '../../../../sales'
import { PDFHeader } from '../../../../../features/ui'
import { firstItem } from '../../../../../utils/listUtils'

type Props = {
  pageIndex: number
  customerId: string
  rows: Data2[]
  summary: { limit: string, available: string, debt: string, creditBalance?: string, netBalance?: string, netBalanceLabel: string }
  pagesAmount: number
  dateFrom?: string | Date
  dateTo?: string | Date
}

export const CreditAccountStatusPDFPage = (props: Props) => {
  const { pageIndex, customerId, rows, summary, pagesAmount, dateFrom, dateTo } = props

  const [customer, setCustomer] = useState<Customer>()
  const [downloadDate] = useState<Date>(new Date())

  const { describeAddress } = addressHelpers
  const { formatDate, formatIdentifier, formatPhone } = useDataTypesHooks()

  const branchLoaders = useBranchLoaders()
  const customerLoaders = useCustomerLoaders()
  const branch = useMemo(() => branchLoaders().findAllBranches()[0], [])

  useEffect(() => {
    customerLoaders().loadCustomer(customerId).then(setCustomer)
  }, [customerId])

  const getFilterLabel = () => {
    const from = dateFrom ? formatDate(new Date(dateFrom)) : null
    const to = dateTo ? formatDate(new Date(dateTo)) : null

    if (from && to) return `Movimientos de Cuenta del ${from} al ${to}:`
    if (from) return `Movimientos de Cuenta desde el ${from}:`
    if (to) return `Movimientos de Cuenta hasta el ${to}:`
    return 'Movimientos de Cuenta:'
  }

  const columns = [
    { name: 'date', label: 'Fecha', width: '15%' },
    { name: 'operationLabel', label: 'Operación', width: '25%' },
    { name: 'debit', label: 'Debe', width: '22.5%' },
    { name: 'credit', label: 'Haber', width: '22.5%' },
  ]

  return (
    <div className={styles.container}>
        <div className={styles.body}>
          <PDFHeader />

          {pageIndex === 0 && (
            <>
              <div className={styles.row}>
                <span className={styles.statusTitle}>
                  Estado de Cuenta Corriente
                </span>
                <div className={styles.date}>
                  Fecha: {formatDate(downloadDate)}
                </div>
              </div>

              <div className={`${styles.row} ${styles.alignStart}`}>
                <div className={styles.infoSection}>
                  <span>Cliente: {customer?.name}</span>
                  <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                  <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                  <span>
                    Teléfono:{' '}
                    {customer && customer.phones.length > 0
                      ? formatPhone(firstItem(customer!.phones))
                      : '-'}
                  </span>
                </div>
                
                <div className={`${styles.infoSection} ${styles.rightSection}`}>
                  <span>Crédito Otorgado: {summary.limit}</span>
                  <span >Crédito Disponible: {summary.available}</span>
                  <span>Deuda Total: {summary.debt}</span>
                  {summary.creditBalance && (
                    <span>Crédito a Favor: {summary.creditBalance}</span>
                  )}
                  {summary.netBalance && (
                    <span>{summary.netBalanceLabel}: {summary.netBalance}</span>
                  )}
                </div>
              </div>

              <div className={`${styles.row} ${styles.alignStart} ${styles.movementsRow}`}>
                <div className={styles.infoSection}>
                  <span>{getFilterLabel()}</span>
                </div>
              </div>
            </>
          )}

          {rows.length > 0 && (
          <>
            <div className={styles.tableRow}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} style={{ width: column.width }}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.date ? formatDate(new Date(row.date as string)) : '-'}</td>
                      <td>{row.operationLabel as string}</td>
                      <td className={styles.cellRight}>{row.debit as string || '-'}</td>
                      <td className={styles.cellRight}>{row.credit as string || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
                {pageIndex + 1} / {pagesAmount}
              </div>
            </>
          )}

          <div className={styles.footer}>
            <div className={styles.separator} />
            <div className={`${styles.row} ${styles.footerRow}`}>
              <span>
                Sucursal {branch?.name}: {branch?.address ? describeAddress(branch.address) : ''}
                {branch?.email ? ` - Mail: ${branch.email}` : ''}
              </span>
              <span className={styles.footerDisclaimer}>
                Este documento es un resumen de cuenta corriente a la fecha indicada.
                Los valores expresados están sujetos a verificaciones administrativas.
                Cualquier discrepancia deberá ser informada a su vendedor.
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }
